import {
  MenuItemType,
  SortBy,
  SortSectionItem,
  SpecialActions,
  SpecialControl,
} from 'common/query-builder/query-block/properties-menu/types';
import {PropertyType, ResourceType} from 'common/report/constants';
import {DATASET_PROPERTY_NAME} from 'common/report/constants';
import {Placement} from 'common/components/tooltip/types';
import {MPProperties} from 'common/constants';
import {
  OrderedTimeUnit,
  SpecialTimeUnit,
  TimeUnit,
  TimeUnitLabels,
} from 'common/widgets/date-range-picker/models/types';

import {PropertySectionItem} from './item-menu-types';

export const EXTRA_PROPERTY_DEFINITIONS_BY_TYPE = {
  [ResourceType.Event]: [
    {
      name: `$date`,
      displayName: `Date`,
      description: `The date and time this event occurred at. All events have this property.`,
      type: PropertyType.Datetime,
      resourceType: ResourceType.Event,
      icon: `type-date`,
    },
    {
      name: `$dataset`,
      displayName: `Project Name`,
      description: `Project Name is a virtual property that you can use to breakdown or filter your data based on the Project ID in which this event/property is tracked. [More about Rollup Projects](https://mixpanel.app.box.com/v/rollup-projects-beta)`,
      type: PropertyType.String,
      resourceType: ResourceType.Event,
    },
  ],
  [ResourceType.User]: [
    {
      name: `$mp_first_event_time`,
      displayName: `First Seen`,
      description: `First Seen is a datetime profile property that captures the timestamp of the very first event for this user/group. This can be used to filter/segment reports by users who were "new" in a particular timeframe. [More about First Seen](https://help.mixpanel.com/hc/en-us/articles/360001355506-Tracking-First-Time-Users#introducing-first-event-time)`,
      type: PropertyType.Datetime,
      resourceType: ResourceType.User,
    },
  ],
};

export const TOP_PEOPLE: PropertySectionItem = {
  type: null,
  propertyDefaultType: null,
  hasCaret: false,
  hasPill: false,
  icon: `profile`,
  isDisabled: false,
  label: `All User Profiles`,
  value: `$all_people`,
  resourceType: ResourceType.User,
  menuItemType: MenuItemType.UserProperties,
};

export const TOP_PATHS: PropertySectionItem = {
  type: PropertyType.NonProperty,
  propertyDefaultType: PropertyType.NonProperty,
  hasCaret: false,
  hasPill: false,
  icon: `top-paths`,
  isDisabled: false,
  label: `Top paths`,
  value: `$paths`,
  resourceType: ResourceType.Other,
  menuItemType: MenuItemType.Other,
};

export const FLOWS_CONVERSION: PropertySectionItem = {
  type: PropertyType.String,
  propertyDefaultType: PropertyType.String,
  hasCaret: false,
  hasPill: false,
  icon: `top-paths`,
  isDisabled: false,
  label: `Conversion`,
  value: `$conversion`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: `Conversion`,
    description: `Find which events are more likely to lead to conversion`,
    entityIcon: `top-paths`,
    extraInfo: [`The flow report must have atleast two steps to do conversion analysis`],
  },
  menuItemType: MenuItemType.Other,
};

export const FLOWS_CONVERSION_VALUES: string[] = [`Converted`, `Did Not Convert`];

export const ALL_USERS_COHORT: PropertySectionItem = {
  type: null,
  propertyDefaultType: null,
  hasCaret: false,
  hasPill: false,
  isDisabled: false,
  label: `All Users`,
  value: `$all_users`,
  resourceType: ResourceType.Cohort,
  menuItemType: MenuItemType.Cohorts,
};

export const DATE_PROPERTY: PropertySectionItem = {
  label: `Date`,
  icon: `type-date`,
  value: MPProperties.Date,
  propertyDefaultType: PropertyType.Datetime,
  type: PropertyType.Datetime,
  resourceType: ResourceType.Event,
  isControlItem: true,
  menuItemType: MenuItemType.EventProperties,
};

export const FIRST_EVENT_TIME_PROPERTY: PropertySectionItem = {
  label: `First Seen`,
  icon: `calendar`,
  value: `$mp_first_event_time`,
  propertyDefaultType: PropertyType.Datetime,
  type: PropertyType.Datetime,
  resourceType: ResourceType.User,
  isControlItem: false,
  menuItemType: MenuItemType.UserProperties,
};

export const DATASET_PROPERTY: PropertySectionItem = {
  label: `Project Name`,
  icon: `star-top-events`,
  value: DATASET_PROPERTY_NAME,
  propertyDefaultType: PropertyType.String,
  type: PropertyType.String,
  resourceType: ResourceType.Event,
  menuItemType: MenuItemType.EventProperties,
};

export const SPECIAL_PROPERTIES: Map<string, PropertySectionItem> = new Map([
  [TOP_PEOPLE.value, TOP_PEOPLE],
  [TOP_PATHS.value, TOP_PATHS],
  [FLOWS_CONVERSION.value, FLOWS_CONVERSION],
  [ALL_USERS_COHORT.value, ALL_USERS_COHORT],
  [DATE_PROPERTY.value, DATE_PROPERTY],
  [FIRST_EVENT_TIME_PROPERTY.value, FIRST_EVENT_TIME_PROPERTY],
  [DATASET_PROPERTY.value, DATASET_PROPERTY],
]);

export const OTHER_OPTIONS: Map<string, PropertySectionItem> = new Map([
  [TOP_PATHS.value, TOP_PATHS],
  [FLOWS_CONVERSION.value, FLOWS_CONVERSION],
]);

export const SPECIAL_CONTROLS: Record<string, SpecialControl> = {
  CREATE_INLINE_COHORT: {
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    icon: `compose`,
    label: `Create Cohort...`,
    resourceType: ResourceType.Cohort,
    value: SpecialActions.CreateInlineCohort,
  },
  CREATE_CUSTOM_EVENT_FORMULA: {
    icon: `compose`,
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    label: `Create a Custom Property...`,
    resourceType: ResourceType.Event,
    value: SpecialActions.CreateCustomEventFormula,
    tooltipData: {
      header: `Create a Custom Property`,
      description: `• Merge and regroup property values\n• Resize numeric ranges\n• Extract patterns`,
      entityIcon: `compose`,
      helpLink: {
        label: `More on custom properties`,
        href: `https://help.mixpanel.com/hc/en-us/articles/360030848432-Custom-Properties`,
      },
    },
  },
  CREATE_CUSTOM_PEOPLE_FORMULA: {
    icon: `compose`,
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    label: `Create a Custom Property...`,
    resourceType: ResourceType.User,
    value: SpecialActions.CreateCustomPeopleFormula,
    tooltipData: {
      header: `Create a Custom Property`,
      description: `• Merge and regroup property values\n• Resize numeric ranges\n• Extract patterns`,
      entityIcon: `compose`,
      helpLink: {
        label: `More on custom properties`,
        href: `https://help.mixpanel.com/hc/en-us/articles/360030848432-Custom-Properties`,
      },
    },
  },
  CREATE_CUSTOM_BEHAVIORAL_PROPERTY: {
    icon: `compose`,
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    label: `Create a Behavior...`,
    resourceType: ResourceType.User,
    value: SpecialActions.CreateCustomBehavioralProperties,
    tooltipData: {
      header: `Create a Behavior`,
      description: `Define properties based on user behavior patterns over time. Common use-cases include: number of times users did an action, summarize user's actions based on a property.`,
      entityIcon: `compose`,
    },
  },
};

export const DATETIME_UNIT_OPTIONS = [
  ...OrderedTimeUnit.filter((unit) => unit !== TimeUnit.Minute),
  ...Object.values(SpecialTimeUnit),
];

export const DATETIME_UNIT_OPTION_SECTION_ITEMS = DATETIME_UNIT_OPTIONS.map((unitValue) => ({
  label: TimeUnitLabels[unitValue],
  value: unitValue,
}));

export const SORT_ON_RELEVANCE: SortSectionItem = {
  label: `Relevancy`,
  icon: `relevancy`,
  value: SortBy.Relevance,
  tooltip: {
    placement: Placement.Right,
    text: `Sorting based on recency and frequency of use in your project`,
    'max-width': `160px`,
  },
};

export const SORT_ON_ALPHA_ASCENDING: SortSectionItem = {
  label: `Name A-Z`,
  icon: `sort-alpha-asc`,
  value: SortBy.NameAsc,
  tooltip: {
    placement: Placement.Right,
    text: `Sorting based on event name starting with A`,
    'max-width': `160px`,
  },
};

export const SORT_ON_ALPHA_DESCENDING: SortSectionItem = {
  label: `Name Z-A`,
  icon: `sort-alpha-desc`,
  value: SortBy.NameDesc,
  tooltip: {
    placement: Placement.Right,
    text: `Sorting based on event name starting with Z`,
    'max-width': `160px`,
  },
};

export const SORT_OPTION_ITEMS: SortSectionItem[] = [
  SORT_ON_RELEVANCE,
  SORT_ON_ALPHA_ASCENDING,
  SORT_ON_ALPHA_DESCENDING,
];

export const AllFilter = {label: `all`, value: {value: `all`}};
